import { ImageSource } from '@inter-ikea-storageone/range';
import theme from 'ui/theme';
import Compressor from 'compressorjs';

export const imageLoadingPlaceholder = `
  &::before {
    content: ' ';
    display: block;
    position: absolute;
    z-index: 2;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${theme.colors.grey[100]};
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: ${theme.colors.grey[800]};
    z-index: 2;
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSJub25lIiBkPSJNMCAwaDI0djI0SDB6bTAgMGgyNHYyNEgwem0yMSAxOWMwIDEuMS0uOSAyLTIgMkg1Yy0xLjEgMC0yLS45LTItMlY1YzAtMS4xLjktMiAyLTJoMTRjMS4xIDAgMiAuOSAyIDIiLz48cGF0aCBmaWxsPSJub25lIiBkPSJNMCAwaDI0djI0SDB6Ii8+PHBhdGggZD0iTTIxIDV2Ni41OWwtMy0zLjAxLTQgNC4wMS00LTQtNCA0LTMtMy4wMVY1YzAtMS4xLjktMiAyLTJoMTRjMS4xIDAgMiAuOSAyIDJ6bS0zIDYuNDJsMyAzLjAxVjE5YzAgMS4xLS45IDItMiAySDVjLTEuMSAwLTItLjktMi0ydi02LjU4bDMgMi45OSA0LTQgNCA0IDQtMy45OXoiLz48L3N2Zz4=');
    background-repeat: no-repeat;
    background-size: 24px 24px;
    width: 24px;
    height: 24px;
  }
`;

export const imageHoverTransition: (prop?: string) => string = (prop = 'all') =>
  `${prop} .3s ease-in-out`;

export const extractImageSource: (
  sources: ImageSource[]
) => { srcset: string; fallbackImage: ImageSource } = sources => {
  // create a srcset attribute based on the image sizes provided
  // @see https://developer.mozilla.org/en-US/docs/Learn/HTML/Multimedia_and_embedding/Responsive_images#How_do_you_create_responsive_images
  const srcset = sources.map(source => `${source.src} ${source.width}w`).join(', ');

  // fallback for browser that does not support srcset
  let fallbackImage = sources.find(source => source.width === 500 || source.height === 500);

  if (!fallbackImage) {
    // fallback to last image in array if there is no 500px image
    fallbackImage = sources[sources.length - 1];
  }

  return { srcset, fallbackImage };
};

export const ratioToPercentString = (ratio: number) => `${100 / ratio}%`;

export const base64toBlob = (base64String: string) => {
  const byteString = atob(base64String.split(',')[1]);
  const buffer = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(buffer);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([buffer], { type: 'image/jpeg' });
};

export const createImageUrlFromBase64 = (base64String: string) => {
  const blob = base64toBlob(base64String);
  return URL.createObjectURL(blob);
};

const compressBlob = async (blob: Blob): Promise<Blob> => {
  return new Promise(resolve => {
    new Compressor(blob, {
      quality: 0.8,
      success: compressedResult => resolve(compressedResult)
    });
  });
};

export const blobToBase64 = async (blob: Blob): Promise<string> => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
};

export const compressImage = async (image: string): Promise<string> => {
  return new Promise(resolve => {
    const blob = base64toBlob(image);
    compressBlob(blob).then(compressedBlob => {
      blobToBase64(compressedBlob).then(compressedImage => {
        resolve(compressedImage);
      });
    });
  });
};
